import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, FileText, Loader2, GripVertical, ChevronUp, ChevronDown, FolderPlus, Folder, Star } from "lucide-react";
import { api, adminHeaders } from "@/lib/api";
import { uploadSample, sampleFileUrl } from "@/lib/samples";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const fmtSize = (b) => (b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.round(b / 1e3)} KB`);

const SampleRow = ({ s, index, total, folders, isCover, onSetCover, onField, onMove, onRemove, drag }) => (
  <li
    data-testid={`sample-row-${s.id}`}
    draggable
    onDragStart={() => drag.start(s.folder_id, index)}
    onDragOver={(e) => {
      e.preventDefault();
      drag.over(s.folder_id, index);
    }}
    onDrop={drag.drop}
    onDragEnd={drag.end}
    className={`flex items-start gap-2 rounded-md border p-2 transition-colors ${drag.overKey === `${s.folder_id}:${index}` ? "border-[#1B66C9] bg-[#1B66C9]/5" : "border-transparent"}`}
  >
    <span className="mt-3 cursor-grab text-muted-foreground" title="Drag to reorder" data-testid={`sample-drag-${s.id}`}>
      <GripVertical size={16} />
    </span>
    <a href={sampleFileUrl(s.id)} target="_blank" rel="noreferrer" className="mt-0.5 flex h-12 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded bg-secondary">
      {s.type === "image" && <img src={sampleFileUrl(s.id)} alt="" className="h-full w-full object-cover" />}
      {s.type === "video" && <video src={`${sampleFileUrl(s.id)}#t=0.5`} muted preload="metadata" className="h-full w-full object-cover" />}
      {s.type === "pdf" && <FileText size={20} className="text-muted-foreground" />}
    </a>
    <div className="min-w-0 flex-1 space-y-1">
      <Input defaultValue={s.title} onBlur={(e) => onField(s, "title", e.target.value)} data-testid={`sample-title-${s.id}`} className="h-8 text-sm" placeholder="Title (optional — blank shows just the picture)" />
      <Input defaultValue={s.caption || ""} onBlur={(e) => onField(s, "caption", e.target.value)} data-testid={`sample-caption-${s.id}`} className="h-8 text-xs" placeholder="Caption — what does this show? (optional)" />
      <div className="flex items-center gap-2">
        <p className="min-w-0 flex-1 truncate text-[11px] text-muted-foreground">
          #{index + 1} · {s.type.toUpperCase()} · {fmtSize(s.size)} · {s.filename}
        </p>
        {folders.length > 1 && (
          <select
            value={s.folder_id}
            onChange={(e) => onField(s, "folder_id", e.target.value)}
            data-testid={`sample-folder-${s.id}`}
            className="h-6 rounded border border-border bg-background px-1 text-[11px]"
            aria-label="Move to folder"
          >
            {folders.map((f) => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
        )}
      </div>
    </div>
    <div className="flex flex-col items-center">
      {s.type !== "pdf" && (
        <button
          type="button"
          onClick={() => onSetCover(s)}
          data-testid={`sample-cover-${s.id}`}
          aria-label={isCover ? "Folder cover" : "Use as folder cover"}
          aria-pressed={isCover}
          title={isCover ? "This is the folder cover" : "Use as folder cover"}
          className={`rounded p-1 hover:bg-secondary ${isCover ? "text-[#F59E0B]" : "text-muted-foreground"}`}
        >
          <Star size={14} fill={isCover ? "currentColor" : "none"} />
        </button>
      )}
      <button type="button" disabled={index === 0} onClick={() => onMove(s.folder_id, index, index - 1)} data-testid={`sample-up-${s.id}`} aria-label="Move up" className="rounded p-1 text-muted-foreground hover:bg-secondary disabled:opacity-30">
        <ChevronUp size={14} />
      </button>
      <button type="button" disabled={index === total - 1} onClick={() => onMove(s.folder_id, index, index + 1)} data-testid={`sample-down-${s.id}`} aria-label="Move down" className="rounded p-1 text-muted-foreground hover:bg-secondary disabled:opacity-30">
        <ChevronDown size={14} />
      </button>
      <button type="button" data-testid={`sample-delete-${s.id}`} onClick={() => onRemove(s)} className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-[#DC2626]" aria-label="Remove sample">
        <Trash2 size={14} />
      </button>
    </div>
  </li>
);

const FolderSection = ({ folder, items, folders, uploads, onRename, onDelete, onUpload, onSetCover, rowProps }) => {
  const fileRef = useRef(null);
  return (
    <section className="rounded-md border border-border" data-testid={`folder-${folder.id}`}>
      <div className="flex flex-wrap items-center gap-2 border-b border-border bg-secondary/40 p-2">
        {folder.cover_id ? (
          <span className="flex h-8 w-11 flex-shrink-0 items-center justify-center overflow-hidden rounded bg-secondary" title="Folder cover" data-testid={`folder-cover-${folder.id}`}>
            {folder.cover_type === "image" && <img src={sampleFileUrl(folder.cover_id)} alt="" className="h-full w-full object-cover" />}
            {folder.cover_type === "video" && <video src={`${sampleFileUrl(folder.cover_id)}#t=0.5`} muted preload="metadata" className="h-full w-full object-cover" />}
            {folder.cover_type === "pdf" && <FileText size={14} className="text-muted-foreground" />}
          </span>
        ) : (
          <Folder size={16} className="text-[#1B66C9]" />
        )}
        <Input
          defaultValue={folder.name}
          onBlur={(e) => onRename(folder, e.target.value)}
          data-testid={`folder-name-${folder.id}`}
          className="h-8 min-w-[12rem] flex-1 font-display text-sm font-bold"
          placeholder="Folder name"
        />
        <span className="text-[11px] text-muted-foreground" data-testid={`folder-count-${folder.id}`}>{items.length} item{items.length === 1 ? "" : "s"}</span>
        <Button type="button" variant="outline" size="sm" data-testid={`folder-upload-${folder.id}`} onClick={() => fileRef.current?.click()} className="font-bold">
          <Upload size={14} className="mr-1.5" /> Upload files
        </Button>
        <input ref={fileRef} type="file" multiple accept="image/*,video/*,application/pdf" data-testid={`folder-file-input-${folder.id}`} className="hidden" onChange={(e) => onUpload(folder, e.target.files, fileRef)} />
        <button type="button" data-testid={`folder-delete-${folder.id}`} onClick={() => onDelete(folder, items.length)} className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-[#DC2626]" aria-label="Delete folder">
          <Trash2 size={14} />
        </button>
      </div>
      <div className="p-2">
        {uploads.map((u) => (
          <div key={u.key} className="mb-2 text-xs" data-testid="sample-upload-progress">
            <div className="flex justify-between">
              <span className="truncate">{u.name}</span>
              <span className={u.error ? "text-[#DC2626]" : ""}>{u.error || `${Math.round(u.progress * 100)}%`}</span>
            </div>
            <div className="mt-1 h-1.5 w-full overflow-hidden rounded bg-secondary">
              <div className="h-full bg-[#1B66C9] transition-[width]" style={{ width: `${u.progress * 100}%` }} />
            </div>
          </div>
        ))}
        {items.length === 0 && uploads.length === 0 ? (
          <p className="py-2 text-center text-xs text-muted-foreground" data-testid={`folder-empty-${folder.id}`}>Empty folder — upload photos, videos or PDFs.</p>
        ) : (
          <ul className="space-y-1" data-testid={`folder-list-${folder.id}`}>
            {items.map((s, i) => (
              <SampleRow key={s.id} s={s} index={i} total={items.length} folders={folders} isCover={s.id === folder.cover_id} onSetCover={(x) => onSetCover(folder, x)} {...rowProps} />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

export const SamplesManager = ({ productId }) => {
  const [folders, setFolders] = useState([]);
  const [items, setItems] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [newName, setNewName] = useState("");
  const [drag, setDragState] = useState({ from: null, overKey: null });
  const bulkRef = useRef(null);

  const load = async () => {
    const r = await api.get(`/products/${productId}/samples`);
    setFolders(r.data.folders);
    setItems(r.data.samples);
  };
  useEffect(() => {
    load();
  }, [productId]); // eslint-disable-line react-hooks/exhaustive-deps

  const inFolder = (fid) => items.filter((s) => s.folder_id === fid);

  const createFolder = async () => {
    const name = newName.trim();
    if (!name) return toast.error("Give the folder a name first.");
    await api.post(`/admin/products/${productId}/sample-folders`, { name }, adminHeaders());
    setNewName("");
    toast.success(`Folder "${name}" created.`);
    await load();
  };

  const onRename = async (folder, value) => {
    const v = value.trim();
    if (!v || v === folder.name) return;
    await api.put(`/admin/sample-folders/${folder.id}`, { name: v }, adminHeaders());
    setFolders((fs) => fs.map((f) => (f.id === folder.id ? { ...f, name: v } : f)));
    toast.success("Folder renamed.");
  };

  const onSetCover = async (folder, s) => {
    if (s.id === folder.cover_id) return;
    const r = await api.put(`/admin/sample-folders/${folder.id}`, { cover_sample_id: s.id }, adminHeaders());
    setFolders((fs) => fs.map((f) => (f.id === folder.id ? { ...f, ...r.data } : f)));
    toast.success(`Cover set for "${folder.name}".`);
  };

  const onDelete = async (folder, count) => {
    const msg = count ? `Delete folder "${folder.name}" and its ${count} item${count === 1 ? "" : "s"}?` : `Delete empty folder "${folder.name}"?`;
    if (!window.confirm(msg)) return;
    await api.delete(`/admin/sample-folders/${folder.id}`, adminHeaders());
    await load();
  };

  const onUpload = async (folder, files, fileRef) => {
    for (const file of Array.from(files)) {
      const key = `${file.name}-${Date.now()}`;
      setUploads((u) => [...u, { key, folderId: folder.id, name: file.name, progress: 0 }]);
      try {
        await uploadSample(productId, file, "", (p) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, progress: p } : x))), folder.id);
        setUploads((u) => u.filter((x) => x.key !== key));
        toast.success(`Uploaded ${file.name} to "${folder.name}"`);
        await load();
      } catch (e) {
        const msg = e?.response?.data?.detail || "Upload failed";
        setUploads((u) => u.map((x) => (x.key === key ? { ...x, error: msg } : x)));
        toast.error(`${file.name}: ${msg}`);
      }
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const onUploadAsFolders = async (files) => {
    let current = folders;
    for (const file of Array.from(files)) {
      const name = file.name.replace(/\.[^.]+$/, "").trim() || file.name;
      let folder = current.find((f) => f.name.toLowerCase() === name.toLowerCase());
      if (!folder) {
        const r = await api.post(`/admin/products/${productId}/sample-folders`, { name }, adminHeaders());
        folder = r.data;
        current = [...current, folder];
        setFolders(current);
      }
      await onUpload(folder, [file], bulkRef);
    }
  };

  const onField = async (s, field, value) => {
    const v = value.trim();
    if (v === (s[field] || "")) return;
    await api.put(`/admin/samples/${s.id}`, { [field]: v }, adminHeaders());
    if (field === "folder_id") {
      toast.success("Moved to folder.");
      await load();
    } else {
      setItems((it) => it.map((x) => (x.id === s.id ? { ...x, [field]: v } : x)));
    }
  };

  const onMove = async (fid, from, to) => {
    const list = inFolder(fid);
    if (to < 0 || to >= list.length) return;
    const [moved] = list.splice(from, 1);
    list.splice(to, 0, moved);
    const reordered = list.map((s, i) => ({ ...s, sort_order: i }));
    setItems((it) => [...it.filter((s) => s.folder_id !== fid), ...reordered].sort((a, b) => a.sort_order - b.sort_order));
    await api.put("/admin/samples/reorder", { ids: reordered.map((x) => x.id) }, adminHeaders());
    toast.success("Order saved.");
  };

  const dragApi = {
    overKey: drag.overKey,
    start: (fid, i) => setDragState({ from: { fid, i }, overKey: null }),
    over: (fid, i) => setDragState((d) => (d.from && d.from.fid === fid ? { ...d, overKey: `${fid}:${i}` } : d)),
    drop: () => {
      if (drag.from && drag.overKey) {
        const [fid, idx] = drag.overKey.split(":");
        if (fid === drag.from.fid && Number(idx) !== drag.from.i) onMove(fid, drag.from.i, Number(idx));
      }
      setDragState({ from: null, overKey: null });
    },
    end: () => setDragState({ from: null, overKey: null }),
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove "${s.title || s.filename}"?`)) return;
    await api.delete(`/admin/samples/${s.id}`, adminHeaders());
    setItems((it) => it.filter((x) => x.id !== s.id));
  };

  return (
    <div className="rounded-md border border-border p-3" data-testid="samples-manager">
      <p className="text-xs text-muted-foreground">
        Customers see these folders on the “Show me a sample” page and pick which ones to view. Name each folder exactly how you want it shown. Click the star on a photo to make it the folder cover. Drag rows (or use the arrows) to set the order inside a folder.
      </p>
      <div className="mt-2 flex gap-2">
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), createFolder())}
          data-testid="new-folder-name"
          className="h-9 text-sm"
          placeholder='New folder name, e.g. "1754 Hybrid – Bottomland camo"'
        />
        <Button type="button" variant="outline" size="sm" data-testid="new-folder-button" onClick={createFolder} className="h-9 font-bold">
          <FolderPlus size={14} className="mr-1.5" /> New folder
        </Button>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" data-testid="upload-as-folders-button" onClick={() => bulkRef.current?.click()} className="h-9 bg-[#1B66C9] font-bold hover:bg-[#144E9C]">
          <Upload size={14} className="mr-1.5" /> Upload as new folders
        </Button>
        <span className="text-[11px] text-muted-foreground">Each file becomes its own folder named from the file name (extension removed).</span>
        <input ref={bulkRef} type="file" multiple accept="image/*,video/*,application/pdf" data-testid="upload-as-folders-input" className="hidden" onChange={(e) => onUploadAsFolders(e.target.files)} />
      </div>
      {folders.length === 0 ? (
        <p className="mt-3 text-center text-xs text-muted-foreground" data-testid="samples-manager-empty">No folders yet — create one above, then upload into it.</p>
      ) : (
        <div className="mt-3 space-y-3" data-testid="folders-list">
          {folders.map((f) => (
            <FolderSection
              key={f.id}
              folder={f}
              folders={folders}
              items={inFolder(f.id)}
              uploads={uploads.filter((u) => u.folderId === f.id)}
              onRename={onRename}
              onDelete={onDelete}
              onUpload={onUpload}
              onSetCover={onSetCover}
              rowProps={{ onField, onMove, onRemove: remove, drag: dragApi }}
            />
          ))}
        </div>
      )}
      {uploads.some((u) => !u.error) && (
        <p className="mt-2 flex items-center gap-1 text-[11px] text-muted-foreground">
          <Loader2 size={12} className="animate-spin" /> Keep this window open until uploads finish.
        </p>
      )}
    </div>
  );
};
